import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Store } from '@ngxs/store';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AuthService } from 'app/core';
import { Permission, ReservedRole } from 'app/core/enums';
import { User } from 'app/core/models/user.types';
import { UserService } from 'app/layout/common/user/user.service';
import { UserState } from 'app/state/user/state';
import { SetUser } from 'app/state/user/actions';

@Component({
  selector: 'notifications-profile',
  templateUrl: './notifications.component.html',
})
export class NotificationsProfileComponent extends OnDestroyMixin implements OnInit {
  private user: User;

  notificationsForm: UntypedFormGroup;
  ui = { isLoading: false };

  constructor(
    private readonly userService: UserService,
    private readonly snackBar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.buildForm();
    this.fetchUserInfo();
  }

  private buildForm(): void {
    this.notificationsForm = new FormGroup({
      weekly_notifications: this.getNewFormControl(),
      daily_notifications: this.getNewFormControl(),
      new_deal_notifications: this.getNewFormControl(),
      deal_status_change_notifications: this.getNewFormControl(),
      new_task_notifications: this.getNewFormControl(),
      task_notifications_mentions_only: this.getNewFormControl(),
      task_notifications_activity_updates: this.getNewFormControl(),
      daily_notifications_preference: this.getNewFormControl(),
      ts_new_releases: this.getNewFormControl(),
      deal_notification_preference: this.getNewFormControl(),
    });
  }

  private fetchUserInfo(): void {
    this.user = this.store.selectSnapshot(UserState.getUser);

    if (this.user.settings) {
      this.notificationsForm.patchValue(this.user.settings.notification_settings);
      this.toggleDailyNotifications(this.notificationsForm.get('daily_notifications').value);
      this.toggleDealNotification();
      this.changeDetectorRef.markForCheck();
    }
  }

  private resetFormAfterSave(): void {
    this.notificationsForm.markAsPristine();
    this.notificationsForm.markAsUntouched();
    this.notificationsForm.patchValue(this.user.settings.notification_settings);
  }

  private getNewFormControl(): FormControl {
    return new FormControl('');
  }

  public isAdmin(): boolean {
    if (this.user?.roles) {
      return this.user.roles.some((role) => role.name === ReservedRole.Admin);
    }

    return false;
  }

  public isFormDisabled(): boolean {
    return this.notificationsForm.pristine || this.ui.isLoading;
  }

  public toggleDailyNotifications(checked: boolean): void {
    if (checked) {
      this.notificationsForm.get('daily_notifications_preference').enable();
    } else {
      this.notificationsForm.get('daily_notifications_preference').disable();
    }
  }

  public toggleDealNotification(): void {
    if (
      this.notificationsForm.get('new_deal_notifications').value ||
      this.notificationsForm.get('deal_status_change_notifications').value
    ) {
      this.notificationsForm.get('deal_notification_preference').enable();
    } else {
      this.notificationsForm.get('deal_notification_preference').disable();
    }
  }

  public saveSettings(applyToEveryone: boolean = false): void {
    this.ui = { isLoading: true };

    const notificationSettings = this.notificationsForm.value;

    const feedbackMessage = applyToEveryone ? 'all users' : 'your account';

    this.userService
      .saveSettings(notificationSettings, applyToEveryone)
      .pipe(untilComponentDestroyed(this))
      .subscribe((user: User) => {
        this.snackBar.open(`Changes have been successfully applied to ${feedbackMessage}.`, null, { duration: 2000 });
        this.ui = { isLoading: false };
        this.store.dispatch(
          new SetUser({
            ...user,
            roles: this.user.roles,
          }),
        );
        this.user = this.store.selectSnapshot(UserState.getUser);
        this.resetFormAfterSave();
        this.changeDetectorRef.markForCheck();
      });
  }
}
